<script lang="ts" setup>
import type { ApalumaChatMessage, ChatMessageFeedback } from '@/types/api-response/chat-session-types.ts';
import { Icon } from '@iconify/vue';
import { useClipboard } from '@vueuse/core';
import { computed, ref } from 'vue';
import { IconLib } from '@/assets/icon-library';

const props = defineProps<{
   message: ApalumaChatMessage;
}>();

const emit = defineEmits<{
   feedbackSubmitted: [feedback: ChatMessageFeedback];
}>();

const { copy, copied, isSupported } = useClipboard({
   legacy: true,
   copiedDuring: 3000,
});

const selectedFeedbackType = ref<boolean>(false);
const showFeedbackSection = ref<boolean>(false);

const copyIcon = computed(() =>
   copied.value ? 'ph:copy-fill' : IconLib.actions.copy,
);

/**
 * Copy the response to the clipboard without HTML tags
 */
function copyResponse() {
   const plainText = props.message.content
      ? props.message.content
            .replace(/<[^>]+>/g, ' ') // replace tags with a space
            .replace(/\s+/g, ' ') // collapse multiple spaces
            .trim()
      : '';

   copy(plainText);
}

/**
 * Handle feedback button click
 */
function handleFeedback(isPositive: boolean) {
   if (props.message.feedback?.isPositive === isPositive) {
      return;
   }

   if (selectedFeedbackType.value === isPositive && showFeedbackSection.value) {
      // If clicking the same feedback type and section is already open, close it
      showFeedbackSection.value = false;
   }
   else {
      // If clicking a different feedback type or section is closed, open with the selected type
      selectedFeedbackType.value = isPositive;
      showFeedbackSection.value = true;
   }
}

/**
 * Handle feedback submitted event
 */
function handleFeedbackSubmitted(feedback: ChatMessageFeedback) {
   showFeedbackSection.value = false;
   emit('feedbackSubmitted', feedback);
}

// Expose state for parent component to render feedback panel
defineExpose({
   showFeedbackSection,
   selectedFeedbackType,
   handleFeedbackSubmitted,
});
</script>

<template>
   <div class="flex items-center gap-1">
      <button
         :disabled="!isSupported"
         aria-label="Copy response to clipboard"
         class="btn-action"
         @click="copyResponse"
      >
         <Icon :icon="copyIcon" class="size-5" />
      </button>
      <button
         aria-label="Give positive feedback"
         class="btn-action"
         :disabled="message.feedback?.isPositive === true"
         @click="handleFeedback(true)"
      >
         <Icon
            :icon="(message.feedback?.isPositive === true) || (selectedFeedbackType === true && showFeedbackSection)
               ? IconLib.chat.thumbsUp.fill
               : IconLib.chat.thumbsUp.outline"
            class="size-5"
         />
      </button>
      <button
         aria-label="Give negative feedback"
         class="btn-action"
         :disabled="message.feedback?.isPositive === false"
         @click="handleFeedback(false)"
      >
         <Icon
            :icon="(message.feedback?.isPositive === false) || (!selectedFeedbackType && showFeedbackSection)
               ? IconLib.chat.thumbsDown.fill
               : IconLib.chat.thumbsDown.outline"
            class="size-5"
         />
      </button>
   </div>
</template>

<style scoped lang="postcss">
.btn-action{
   @apply flex text-black/75 justify-center items-center p-1 rounded-full hover:bg-neutral-dark/50;
   @apply disabled:cursor-not-allowed;
}

.btn-action-with-text{
   @apply flex items-center py-1 px-2 gap-1 rounded-full hover:bg-neutral-dark/50;
   @apply font-medium text-sm text-black/75;
}
</style>
